import { jwtDecode } from 'jwt-decode'

const SESSION_AUTH_KEY = 'scribbl_session_auth'
const SESSION_LOGGED_OUT_KEY = 'scribbl_session_logged_out'
export const OAUTH_STATE_KEY = 'scribbl_google_oauth_state'
export const OAUTH_PROVIDER_KEY = 'scribbl_oauth_provider'

type StoredSession = {
  email?: string
  exp: number
  token: string
  provider?: 'google' | 'azure' | 'apple'
}

type JwtPayload = {
  exp?: number
  email?: string
  sub?: string
}

const getSessionStorage = () => {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

const getLegacySessionStorage = () => {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

let memorySession: StoredSession | null = null

const parseJwtPayload = (token: string): JwtPayload | null => {
  try {
    return jwtDecode<JwtPayload>(token)
  } catch {
    return null
  }
}

const deriveJwtExp = (token: string) => {
  const payload = parseJwtPayload(token)
  if (!payload?.exp || Number.isNaN(payload.exp)) return null
  return payload.exp * 1000
}

const deriveJwtEmail = (token: string) => {
  const payload = parseJwtPayload(token)
  const candidate = payload?.email || payload?.sub
  return typeof candidate === 'string' && candidate.includes('@') ? candidate : null
}

const getValidSession = (): StoredSession | null => {
  const storage = getSessionStorage()
  if (!storage) return null

  if (storage.getItem(SESSION_LOGGED_OUT_KEY) === '1') {
    memorySession = null
    return null
  }

  const saved = storage.getItem(SESSION_AUTH_KEY)
  if (!saved) {
    const legacyStorage = getLegacySessionStorage()
    const legacySaved = legacyStorage?.getItem(SESSION_AUTH_KEY)
    if (!legacySaved) {
      memorySession = null
      return null
    }
    storage.setItem(SESSION_AUTH_KEY, legacySaved)
    legacyStorage?.removeItem(SESSION_AUTH_KEY)
  }

  try {
    const parsed = JSON.parse(storage.getItem(SESSION_AUTH_KEY) as string) as Partial<StoredSession>
    if (!parsed?.token) {
      return memorySession && memorySession.exp > Date.now() ? memorySession : null
    }
    const parsedExp =
      typeof parsed.exp === 'number' && !Number.isNaN(parsed.exp) ? parsed.exp : null
    const normalizedExp =
      parsedExp && parsedExp < 100000000000 ? parsedExp * 1000 : parsedExp
    const derivedExp = deriveJwtExp(parsed.token)
    const exp =
      normalizedExp && normalizedExp > Date.now()
        ? normalizedExp
        : derivedExp
    if (!exp) throw new Error('Missing token expiry')
    if (exp < Date.now()) {
      return null
    }
    const email = parsed.email || deriveJwtEmail(parsed.token) || undefined
    memorySession = { token: parsed.token, exp, email, provider: parsed.provider }
    storage.setItem(SESSION_AUTH_KEY, JSON.stringify({ email, exp, token: parsed.token, provider: parsed.provider }))
    return memorySession
  } catch (err) {
    console.error('Failed to parse session auth', err)
    return null
  }
}

export const getSessionAuth = (): string | null => {
  return getValidSession()?.email ?? null
}

export const getSessionProvider = (): 'google' | 'azure' | 'apple' | null => {
  return getValidSession()?.provider ?? null
}

export const getSessionExpiry = (): number | null => {
  return getValidSession()?.exp ?? null
}

export const getSessionToken = (): string | null => getValidSession()?.token ?? null

export const hasSessionMetadata = (): boolean => {
  const storage = getSessionStorage()
  if (!storage || storage.getItem(SESSION_LOGGED_OUT_KEY) === '1') return false
  const saved = storage.getItem(SESSION_AUTH_KEY)
  if (saved) return true
  const legacySaved = getLegacySessionStorage()?.getItem(SESSION_AUTH_KEY)
  return Boolean(legacySaved)
}

export const getAuthHeaders = (): Record<string, string> => {
  const token = getSessionToken()
  if (!token) return {}
  return { Authorization: `Bearer ${token}` }
}

export const setSessionAuth = (value: { email?: string | null; exp?: number | null; token: string; provider?: 'google' | 'azure' | 'apple' | null }) => {
  const storage = getSessionStorage()
  if (!storage) return
  const providedExp =
    typeof value.exp === 'number' && !Number.isNaN(value.exp) ? value.exp : null
  const normalizedExp =
    providedExp && providedExp < 100000000000 ? providedExp * 1000 : providedExp
  const derivedExp = deriveJwtExp(value.token)
  const exp = normalizedExp && normalizedExp > Date.now() ? normalizedExp : derivedExp
  if (!exp) {
    console.error('Missing token expiry')
    return
  }
  const email = value.email || deriveJwtEmail(value.token) || undefined
  storage.removeItem(SESSION_LOGGED_OUT_KEY)
  memorySession = { email, exp, token: value.token, provider: value.provider ?? undefined }
  storage.setItem(SESSION_AUTH_KEY, JSON.stringify({ email, exp, token: value.token, provider: value.provider }))
  getLegacySessionStorage()?.removeItem(SESSION_AUTH_KEY)
  getLegacySessionStorage()?.removeItem(SESSION_LOGGED_OUT_KEY)
}

export const clearSessionAuth = () => {
  const storage = getSessionStorage()
  memorySession = null
  if (!storage) return
  storage.removeItem(SESSION_AUTH_KEY)
  getLegacySessionStorage()?.removeItem(SESSION_AUTH_KEY)
}

export const logoutSession = () => {
  const storage = getSessionStorage()
  memorySession = null
  if (!storage) return
  storage.setItem(SESSION_LOGGED_OUT_KEY, '1')
  storage.removeItem(SESSION_AUTH_KEY)
  const legacyStorage = getLegacySessionStorage()
  legacyStorage?.setItem(SESSION_LOGGED_OUT_KEY, '1')
  legacyStorage?.removeItem(SESSION_AUTH_KEY)
}

export const createOAuthState = (provider?: 'google' | 'azure' | 'apple') => {
  const storage = getSessionStorage()
  if (!storage) return null
  const nonce = typeof crypto !== 'undefined' && 'getRandomValues' in crypto
    ? Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('')
    : Date.now().toString(36) + Math.random().toString(36).slice(2)
  const value = provider ? `${provider}:${nonce}` : nonce
  storage.setItem(OAUTH_STATE_KEY, value)
  getLegacySessionStorage()?.setItem(OAUTH_STATE_KEY, value)
  return value
}

export const parseProviderFromState = (state: string | null): 'google' | 'azure' | 'apple' | null => {
  if (!state) return null
  const sep = state.indexOf(':')
  if (sep === -1) return null
  const provider = state.slice(0, sep)
  if (provider === 'google' || provider === 'azure' || provider === 'apple') return provider
  return null
}

export const getOAuthState = () => {
  const storage = getSessionStorage()
  if (!storage) return null
  const value = storage.getItem(OAUTH_STATE_KEY)
  if (value && value.trim()) return value
  const legacyValue = getLegacySessionStorage()?.getItem(OAUTH_STATE_KEY)
  if (!legacyValue || !legacyValue.trim()) return null
  storage.setItem(OAUTH_STATE_KEY, legacyValue)
  getLegacySessionStorage()?.removeItem(OAUTH_STATE_KEY)
  return legacyValue
}

export const clearOAuthState = () => {
  const storage = getSessionStorage()
  if (!storage) return
  storage.removeItem(OAUTH_STATE_KEY)
  storage.removeItem(OAUTH_PROVIDER_KEY)
  const legacyStorage = getLegacySessionStorage()
  legacyStorage?.removeItem(OAUTH_STATE_KEY)
  legacyStorage?.removeItem(OAUTH_PROVIDER_KEY)
}

export const setOAuthProvider = (provider: 'google' | 'azure' | 'apple') => {
  const storage = getSessionStorage()
  if (!storage) return
  storage.setItem(OAUTH_PROVIDER_KEY, provider)
  getLegacySessionStorage()?.setItem(OAUTH_PROVIDER_KEY, provider)
}

export const getOAuthProvider = () => {
  const storage = getSessionStorage()
  if (!storage) return null
  const value = storage.getItem(OAUTH_PROVIDER_KEY)
  if (value) return value as 'google' | 'azure' | 'apple'
  const legacyValue = getLegacySessionStorage()?.getItem(OAUTH_PROVIDER_KEY) as 'google' | 'azure' | 'apple' | null
  if (!legacyValue) return null
  storage.setItem(OAUTH_PROVIDER_KEY, legacyValue)
  getLegacySessionStorage()?.removeItem(OAUTH_PROVIDER_KEY)
  return legacyValue
}
